import React from 'react';
import { clsx } from 'clsx';

interface RadioOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

interface RadioGroupProps {
  name: string;
  options: RadioOption[];
  value?: string;
  onChange?: (value: string) => void;
  label?: string;
  error?: string;
  orientation?: 'horizontal' | 'vertical';
  className?: string;
}

export function RadioGroup({
  name,
  options,
  value,
  onChange,
  label,
  error,
  orientation = 'vertical',
  className,
}: RadioGroupProps) {
  return (
    <fieldset className={className}>
      {label && (
        <legend className="text-sm font-semibold leading-6 text-gray-900 dark:text-gray-100">
          {label}
        </legend>
      )}
      <div
        className={clsx(
          'mt-2',
          orientation === 'horizontal' ? 'flex flex-wrap gap-4' : 'space-y-3'
        )}
      >
        {options.map((option) => (
          <div key={option.value} className="flex items-start">
            <div className="flex h-6 items-center">
              <input
                id={`${name}-${option.value}`}
                name={name}
                type="radio"
                value={option.value}
                checked={value === option.value}
                disabled={option.disabled}
                onChange={(e) => onChange?.(e.target.value)}
                className={clsx(
                  'h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-600 dark:border-gray-700',
                  error && 'border-red-300',
                  option.disabled && 'cursor-not-allowed opacity-50'
                )}
              />
            </div>
            <div className="ml-3 text-sm leading-6">
              <label
                htmlFor={`${name}-${option.value}`}
                className={clsx(
                  'font-medium',
                  option.disabled ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-gray-100'
                )}
              >
                {option.label}
              </label>
              {option.description && (
                <p className="text-gray-500 dark:text-gray-400">{option.description}</p>
              )}
            </div>
          </div>
        ))}
      </div>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </fieldset>
  );
}

interface RadioProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: string;
  description?: string;
}

export function Radio({ label, description, className, id, ...props }: RadioProps) {
  const inputId = id || label.toLowerCase().replace(/\s+/g, '-');

  return (
    <div className="flex items-start">
      <div className="flex h-6 items-center">
        <input
          id={inputId}
          type="radio"
          className={clsx(
            'h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-600 dark:border-gray-700',
            className
          )}
          {...props}
        />
      </div>
      <div className="ml-3 text-sm leading-6">
        <label htmlFor={inputId} className="font-medium text-gray-900 dark:text-gray-100">
          {label}
        </label>
        {description && <p className="text-gray-500 dark:text-gray-400">{description}</p>}
      </div>
    </div>
  );
}
